<template>
    <Container>
        <div class="my-4 flex items-center justify-between gap-3">
            <div>
                <h1 class="text-2xl font-bold text-white">Create Artist Battle</h1>
                <p class="mt-1 text-sm text-nomad-300">Choose two artists, then give the battle a title and description.</p>
            </div>
            <Link href="/backstage/artist-battles"
                class="shrink-0 rounded-md bg-nomad-900 px-4 py-2 text-white transition-colors hover:bg-nomad-800">
                Cancel
            </Link>
        </div>

        <form @submit.prevent="submit" class="max-w-4xl space-y-4">
            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-4 text-lg font-semibold text-nomad-200">Battle details</h2>
                <div class="grid gap-4">
                    <label class="block text-sm font-medium text-nomad-300">
                        Title <span class="text-red-400">*</span>
                        <input v-model="form.title" type="text" maxlength="255" required
                            class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="e.g. Artist of the Week" />
                        <span v-if="errors.title" class="mt-1 block text-sm text-red-400">{{ errors.title }}</span>
                    </label>

                    <label class="block text-sm font-medium text-nomad-300">
                        Description <span class="text-red-400">*</span>
                        <textarea v-model="form.description" rows="4" maxlength="10000" required
                            class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Tell voters about this matchup." />
                        <span v-if="errors.description" class="mt-1 block text-sm text-red-400">{{ errors.description }}</span>
                    </label>
                </div>
            </section>

            <section class="rounded-xl bg-nomad-900 p-6">
                <h2 class="mb-1 text-lg font-semibold text-nomad-200">Competing artists</h2>
                <p class="mb-4 text-sm text-nomad-300">Search and select two different artists. Their images are shown in the matchup.</p>

                <div class="grid gap-3 sm:grid-cols-2">
                    <div v-for="(artist, index) in selectedArtists" :key="index"
                        class="flex min-h-20 items-center gap-3 rounded-lg bg-nomad-800 p-3">
                        <template v-if="artist">
                            <img :src="artist.image || fallbackImage" :alt="artist.name"
                                class="h-14 w-14 rounded-lg object-cover" />
                            <div class="min-w-0 flex-1">
                                <p class="truncate font-semibold text-white">{{ artist.stage_name || artist.name }}</p>
                                <p class="truncate text-sm text-nomad-300">{{ artist.name }}</p>
                            </div>
                            <button type="button" @click="removeArtist(index)"
                                class="rounded-md p-2 text-red-400 transition-colors hover:bg-red-500/10"
                                :aria-label="`Remove ${artist.name}`">
                                <span class="icon-x" aria-hidden="true"></span>
                            </button>
                        </template>
                        <p v-else class="text-sm text-nomad-400">Artist {{ index + 1 }} not selected</p>
                    </div>
                </div>
                <span v-if="errors.artist_one_id || errors.artist_two_id" class="mt-2 block text-sm text-red-400">
                    {{ errors.artist_one_id || errors.artist_two_id }}
                </span>

                <label class="mt-5 block text-sm font-medium text-nomad-300">
                    Find an artist
                    <input v-model="search" type="search" autocomplete="off"
                        class="mt-2 w-full rounded-md border border-nomad-700 bg-nomad-800 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent"
                        placeholder="Search by artist or stage name" />
                </label>
                <p v-if="artistError" class="mt-2 text-sm text-red-400">{{ artistError }}</p>
                <p v-else-if="loadingArtists" class="mt-2 text-sm text-nomad-300">Searching artists...</p>
                <ul v-else-if="search.trim() && artists.length" class="mt-3 grid max-h-64 gap-2 overflow-y-auto">
                    <li v-for="artist in artists" :key="artist.id">
                        <button type="button" @click="selectArtist(artist)"
                            :disabled="selectedArtists.some((selected) => selected?.id === artist.id)"
                            class="flex w-full items-center gap-3 rounded-lg bg-nomad-800 p-3 text-left transition-colors hover:bg-nomad-700 disabled:cursor-not-allowed disabled:opacity-50">
                            <img :src="artist.image || fallbackImage" :alt="artist.name"
                                class="h-11 w-11 rounded-lg object-cover" />
                            <span class="min-w-0 flex-1">
                                <span class="block truncate font-medium text-white">{{ artist.stage_name || artist.name }}</span>
                                <span class="block truncate text-sm text-nomad-300">{{ artist.name }}</span>
                            </span>
                            <span v-if="selectedArtists.some((selected) => selected?.id === artist.id)"
                                class="text-sm text-nomad-accent">Selected</span>
                        </button>
                    </li>
                </ul>
                <p v-else-if="search.trim() && !loadingArtists" class="mt-3 text-sm text-nomad-400">No artists found.</p>
            </section>

            <p v-if="submitError" class="rounded-lg bg-red-500/10 p-3 text-sm text-red-300">{{ submitError }}</p>
            <button type="submit" :disabled="processing || !form.artist_one_id || !form.artist_two_id"
                class="rounded-md bg-nomad-accent px-6 py-3 font-semibold text-nomad-900 transition-colors hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-50">
                {{ processing ? 'Creating...' : 'Create draft battle' }}
            </button>
        </form>
    </Container>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';
import Container from '@/Pages/Backstage/Container.vue';

const fallbackImage = 'https://open.nomadlyrics.com/assets/images/no-artist.png';
const form = ref({ title: '', description: '', artist_one_id: null, artist_two_id: null });
const selectedArtists = ref([null, null]);
const search = ref('');
const artists = ref([]);
const errors = ref({});
const artistError = ref('');
const submitError = ref('');
const loadingArtists = ref(false);
const processing = ref(false);
let searchTimeout;
let activeSearch = 0;

watch(search, (value) => {
    clearTimeout(searchTimeout);
    const searchId = ++activeSearch;
    artistError.value = '';
    if (!value.trim()) {
        artists.value = [];
        loadingArtists.value = false;
        return;
    }
    loadingArtists.value = true;
    searchTimeout = setTimeout(() => fetchArtists(value, searchId), 250);
});

onBeforeUnmount(() => clearTimeout(searchTimeout));

const selectedIds = computed(() => selectedArtists.value.map((artist) => artist?.id));

async function fetchArtists(value, searchId) {
    try {
        const response = await axios.get('/api/backstage/artist-battles/artists', {
            params: { search: value },
        });
        if (searchId === activeSearch) {
            artists.value = response.data.data || [];
        }
    } catch (error) {
        if (searchId === activeSearch) {
            artistError.value = error.response?.data?.message || 'Unable to load artists. Please try again.';
        }
    } finally {
        if (searchId === activeSearch) loadingArtists.value = false;
    }
}

function selectArtist(artist) {
    if (selectedIds.value.includes(artist.id)) return;
    const index = selectedArtists.value.findIndex((selected) => !selected);
    if (index === -1) return;
    selectedArtists.value[index] = artist;
    form.value[index === 0 ? 'artist_one_id' : 'artist_two_id'] = artist.id;
    search.value = '';
    artists.value = [];
}

function removeArtist(index) {
    selectedArtists.value[index] = null;
    form.value[index === 0 ? 'artist_one_id' : 'artist_two_id'] = null;
}

async function submit() {
    processing.value = true;
    errors.value = {};
    submitError.value = '';
    try {
        await axios.post('/api/backstage/artist-battles', form.value);
        router.visit('/backstage/artist-battles');
    } catch (error) {
        const response = error.response;
        if (response?.status === 422 && response.data.errors) {
            errors.value = Object.fromEntries(
                Object.entries(response.data.errors).map(([field, messages]) => [field, messages[0]])
            );
            submitError.value = response.data.message || 'Please correct the highlighted fields.';
        } else {
            submitError.value = response?.data?.message || 'Unable to create the battle. Please try again.';
        }
    } finally {
        processing.value = false;
    }
}
</script>
